import type { EventId, BuildId, BoatId, TeamId, ReleaseId, BoatCode, WingConfig, PlatformWeighingId } from "@/types/identifiers";

export const eventKeys = {
  all: ["events"] as const,
  list: (season?: number) => [...eventKeys.all, "list", { season }] as const,
  detail: (id: EventId) => [...eventKeys.all, "detail", id] as const,
};

export const buildKeys = {
  all: ["builds"] as const,
  byEvent: (eventId: EventId) =>
    [...buildKeys.all, "event", eventId] as const,
  detail: (id: BuildId) => [...buildKeys.all, "detail", id] as const,
};

export const calibrationKeys = {
  all: ["calibrations"] as const,
  byBuild: (buildId: BuildId) =>
    [...calibrationKeys.all, "build", buildId] as const,
  history: (buildId: BuildId) =>
    [...calibrationKeys.all, "history", buildId] as const,
  live: (buildId: BuildId) =>
    [...calibrationKeys.all, "live", buildId] as const,
};

export const boatKeys = {
  all: ["boats"] as const,
  list: (teamId?: TeamId) => [...boatKeys.all, "list", { teamId }] as const,
  detail: (id: BoatId) => [...boatKeys.all, "detail", id] as const,
};

export const buildPhotoKeys = {
  all: ["build-photos"] as const,
  byBuild: (buildId: BuildId) =>
    [...buildPhotoKeys.all, "build", buildId] as const,
};

export const weighingPhotoKeys = {
  all: ["weighing-photos"] as const,
  byWeighing: (weighingId: PlatformWeighingId) =>
    [...weighingPhotoKeys.all, "weighing", weighingId] as const,
};

export const nvpKeys = {
  status: ["nvp-status"] as const,
};

export const adminKeys = {
  all: ["admin"] as const,
  calibrationSync: () =>
    [...adminKeys.all, "calibration-sync", "teams"] as const,
};

export const releaseKeys = {
  all: ["releases"] as const,
  list: () => [...releaseKeys.all, "list"] as const,
  detail: (id: ReleaseId) => [...releaseKeys.all, "detail", id] as const,
};

export const eventReleaseKeys = {
  all: ["event-releases"] as const,
  byEvent: (eventId: EventId) =>
    [...eventReleaseKeys.all, "event", eventId] as const,
  detail: (eventId: EventId, releaseId: ReleaseId) =>
    [...eventReleaseKeys.all, "detail", eventId, releaseId] as const,
  withCommonCals: () =>
    [...eventReleaseKeys.all, "with-common-cals"] as const,
};

export const commonCalsKeys = {
  all: ["common-cals"] as const,
  teamStatus: (eventId: EventId) =>
    [...commonCalsKeys.all, "teams", eventId] as const,
};

export const toeCheckKeys = {
  all: ["toe-checks"] as const,
  byBuild: (buildId: BuildId) =>
    [...toeCheckKeys.all, "build", buildId] as const,
};

export const yawCheckKeys = {
  all: ["yaw-checks"] as const,
  byBuild: (buildId: BuildId) =>
    [...yawCheckKeys.all, "build", buildId] as const,
  previousEvent: (buildId: BuildId) =>
    [...yawCheckKeys.all, "previous-event", buildId] as const,
};

export const fleetMeasurementKeys = {
  all: ["fleet-measurements"] as const,
  yaw: (eventId: EventId) =>
    [...fleetMeasurementKeys.all, "yaw", eventId] as const,
  steering: (eventId: EventId) =>
    [...fleetMeasurementKeys.all, "steering", eventId] as const,
  dbHeight: (eventId: EventId) =>
    [...fleetMeasurementKeys.all, "db-height", eventId] as const,
};

export const boatSyncKeys = {
  all: ["boat-sync"] as const,
  bySyncType: (syncType: string) =>
    [...boatSyncKeys.all, syncType] as const,
  // The leaf identifies what's being compared — a boat for board cals, a build
  // for wing cals — so it's a generic string, not specifically a BoatCode.
  compare: (syncType: string, id: BoatCode | string) =>
    [...boatSyncKeys.all, syncType, id] as const,
};

export const platformWeighingKeys = {
  all: ["platform-weighings"] as const,
  byBoat: (boatId: BoatId) =>
    [...platformWeighingKeys.all, "boat", boatId] as const,
  fleet: () => [...platformWeighingKeys.all, "fleet"] as const,
};

export const wingWeighingKeys = {
  all: ["wing-weighings"] as const,
  byBoat: (boatId: BoatId) =>
    [...wingWeighingKeys.all, "boat", boatId] as const,
  byBoatAndConfig: (boatId: BoatId, config: WingConfig) =>
    [...wingWeighingKeys.all, "boat", boatId, config] as const,
  fleet: (config: WingConfig) =>
    [...wingWeighingKeys.all, "fleet", config] as const,
};

export const correctorStateKeys = {
  all: ["corrector-states"] as const,
  byBuild: (buildId: BuildId) =>
    [...correctorStateKeys.all, "build", buildId] as const,
};

export const fleetEqualizationKeys = {
  all: ["fleet-equalization"] as const,
  byEvent: (eventId: EventId) =>
    [...fleetEqualizationKeys.all, "event", eventId] as const,
};

export const fleetReportKeys = {
  all: ["fleet-report"] as const,
};

export const metricsKeys = {
  all: ["metrics"] as const,
  byBuild: (buildId: BuildId) => [...metricsKeys.all, "build", buildId] as const,
  history: (buildId: BuildId, kind: string) =>
    [...metricsKeys.all, "history", buildId, kind] as const,
  fleet: ["metrics", "fleet"] as const,
};

export const scheduleKeys = {
  all: ["schedule"] as const,
  byEvent: (eventId: EventId) =>
    [...scheduleKeys.all, "event", eventId] as const,
};

export const wingCalKeys = {
  all: ["wing-cal-sync"] as const,
  byEvent: (eventId: EventId) =>
    [...wingCalKeys.all, "event", eventId] as const,
  history: (buildId: BuildId) =>
    [...wingCalKeys.all, "build", buildId, "history"] as const,
};

export const wingNvpKeys = {
  status: ["wing-nvp", "status"] as const,
};
